import { computeQuality, qualityBand, qualityColorClasses, qualityTooltip } from '@/lib/qualityScore';
import InfoTip from '@/components/InfoTip';
import QualityBreakdownPopover from '@/components/QualityBreakdownPopover';

/**
 * QualityBadge — compact, reusable Dividend Quality Score pill (0–100).
 * Renders an A+/A/B/C/D/F letter + the numeric score, colour-coded
 * by band.
 *
 * Two display modes:
 *   • interactive=false (default) — hover shows a per-pillar tooltip
 *     via InfoTip. Use on dense list surfaces where a click would
 *     conflict with row navigation.
 *   • interactive=true — click opens a full QualityBreakdownPopover
 *     with bars, fundamental bonuses, and structural penalties. Use
 *     on Portfolio/Stock-Modal/Screener where the user might want a
 *     one-click gut-check before adding to a position.
 *
 * Designed to live ALONGSIDE the existing Income Risk badge — they
 * answer different questions (risk = "how shaky" vs quality =
 * "how attractive").
 *
 * Props:
 *   • stock        — full stock-cache record
 *   • size         — 'xs' | 'sm' | 'md' (default 'sm')
 *   • showLabel    — also render the band word (e.g. "Strong"). Default false.
 *   • interactive  — render as a click-to-open popover trigger (default false)
 *   • testIdSuffix — appended to data-testid for unique targeting
 */
export default function QualityBadge({
  stock,
  size = 'sm',
  showLabel = false,
  interactive = false,
  testIdSuffix = '',
}) {
  const quality = computeQuality(stock);
  const band = qualityBand(quality?.score);
  const cls = qualityColorClasses(band.tone);
  const tip = qualityTooltip(quality);
  const ticker = stock?.ticker || '';

  // Sizing knobs — kept centralised so every surface stays consistent.
  // The `xs` text was bumped from 8px → 9px in May 2026 per user
  // request to improve readability on the stock-list surfaces (main
  // table, watchlist, portfolio table, screener) without touching the
  // larger `md` size used inside the StockModal header.
  const sizeCls = {
    xs: 'px-1 py-px text-[9px] gap-0.5',
    sm: 'px-1.5 py-0.5 text-[10px] gap-1',
    md: 'px-2 py-0.5 text-[11px] gap-1',
  }[size] || 'px-1.5 py-0.5 text-[10px] gap-1';

  const dotCls = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2 h-2',
  }[size] || 'w-2 h-2';

  const pill = (
    <span
      className={`inline-flex items-center font-bold uppercase tracking-wider ring-1 rounded-full ${sizeCls} ${cls.bg} ${cls.text} ${cls.ring} ${interactive ? 'cursor-pointer hover:ring-2 transition-all' : ''}`}
      data-testid={`quality-badge${testIdSuffix ? `-${testIdSuffix}` : ''}${ticker ? `-${ticker}` : ''}`}
    >
      <span className={`rounded-full ${dotCls} ${cls.dot}`} />
      {quality ? quality.score : '—'}
      <span className="opacity-70 font-mono">{band.short}</span>
      {showLabel && <span className="font-semibold normal-case ml-0.5 hidden sm:inline">{band.label}</span>}
    </span>
  );

  if (interactive) {
    // Stop click propagation so opening the popover from inside a
    // clickable row (e.g., portfolio ticker row) doesn't also fire
    // the row's onClick. Native `title` gives the per-pillar hover
    // tooltip (same content as the non-interactive InfoTip mode), so
    // users get BOTH hover info AND click-to-open-popover from a
    // single pill — no UX trade-off between the two surfaces.
    return (
      <QualityBreakdownPopover stock={stock}>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-full"
          aria-label={`Open quality breakdown for ${ticker}`}
          title={tip}
        >
          {pill}
        </button>
      </QualityBreakdownPopover>
    );
  }

  return (
    <InfoTip
      term={`Dividend Quality · ${ticker}`}
      def={tip}
      className="!no-underline"
      iconSize={9}
      showIcon={false}
    >
      {pill}
    </InfoTip>
  );
}
